import {
  ProductTypeName,
  ShippingMethod,
  WarehouseShippingMethod,
} from '@electricjs/core_entity-client';

import { Country } from '@electricjs/arc';

import { SortingParams } from '@/types/common';
import { type Product } from '@/types/products';
import { type StorefrontAsset } from '@/types/assets';
import { ProductOriginName } from '@/redux/slices/productApiSlice';

//
// Storefront's general types
//

export enum StorefrontSteps {
  SHOPPING = 'SHOPPING',
  CART = 'CART',
  CHECKOUT = 'CHECKOUT',
  ORDER_PLACED = 'ORDER_PLACED',
}

export enum Store {
  Catalog = 'catalog',
  Asset = 'asset',
}

//
// Storefront's ProductType groups (the specific grouping used in Storefront filters)
//

/** It's the group of "product types" in Storefront (selected through the big buttons on the top)
 *  For example, the group "Keyboards and Mice" has 3 product types (Keyboards, Mouses, Combos).
 *  But some groups may have only 1 product type (e.g., Monitors).
 */
export enum StorefrontProductTypeGroup {
  Laptops = 'laptops',
  Monitors = 'monitors',
  Desktops = 'desktops',
  Tablets = 'tablets',
  Phones = 'phones',
  KeyboardMouse = 'keyboardMouseRelated',
  Docks = 'docks',
  AudioVideo = 'audioVideo',
  Misc = 'misc',
}

//
// Storefront's filters
//

/**
 *  Enum for the keys of the Dynamic Filters.
 *  Can't be imported from the core_entity-client, because it's a dictionary in the backend.
 *  Also, the frontend adds an extra filter (TYPE) in some product type groups. (e.g., Keyboards and Mice).
 * */
export enum DynamicFilterKey {
  PRICE = 'price_range',
  RAM = 'ram_range',
  SIZE = 'size_range',
  MANUFACTURER = 'manufacturer',
  PROCESSOR = 'processor',
  STORAGE = 'ssd_range',
  COLOR = 'color',
  CONNECTIVITY = 'connectivity',
  CARRIER = 'carrier_compatibility',
  TELEPHONE_TYPE = 'telephone_type',
  LINES_SUPPORTED = 'lines_supported',
  POWER_OVER_ETHERNET = 'power_over_ethernet',
  KEYBOARD_LANGUAGE = 'keyboard_language',
  /**
   *  The `product_type` key does not come inside the `filter` object in the product list response, but it's used as query param in the product list request.
   *  When used as query param, its value come both from the type group selector as well as from the "inner changes" (using the dynamic filter dropdown).
   *  But its official state is always handled directly by the productType handler (not by the dynamic filter handler). */
  TYPE = 'product_type',
}

export type DynamicFilters = {
  [filterKey in DynamicFilterKey]?: string[]; // selected values of each filter (e.g manufacturer, price_range)
};

export type DynamicFiltersByProductType = {
  [productType in ProductTypeName]?: DynamicFilters; // dynamic filters for each product type
};

export type StoreFiltersSelections = {
  selectedProductTypes: ProductTypeName[];
  dynamicFiltersSelectedValues?: DynamicFilters;
  selectedSorting: SortingParams;
  selectedPage: number;
  needsWindowsZtpAvailable?: boolean;
  specificProductOriginName?: ProductOriginName;
  countryCode?: Country;
};

//
// Storefront's cart
//

/** Represents one entry in the Catalog cart */
export type CatalogCartEntry = {
  item: Product;
  quantity: number;
  selectedWarranty?: Product;
  /** Used only by Quote Checkout */
  quoteAcceptedLineItemId?: string;
};

/** Represents one entry in the Asset cart */
export type AssetCartEntry = {
  item: StorefrontAsset;
};

/** It's an object with all carts */
export type FinalCart = {
  catalogCart: CatalogCartEntry[];
  assetCart?: AssetCartEntry[];
};

//
// Storefront's shipping
//

export type CatalogShippingMethodOption = {
  label: string;
  value: ShippingMethod;
  /** ETA is only for international quotes/orders */
  eta?: {
    min: Date;
    max: Date;
  };
};

export type WarehouseShippingMethodOption = {
  label: string;
  value: WarehouseShippingMethod;
};

//
// Storefront's shopping country
//

export type ShoppingCountryOption = {
  label: string;
  /** Country ISO code or "other" */
  value: Country | 'other';
};
